import {
  addCurrentAuthHeader,
  type SessionService,
} from '@efdigital/ll-addon-auth';
import type { Apis } from '@efdigital/ll-addon-auth/types/apis';

/**
 * A cohort announcement as returned by the end-user messages API.
 *
 * Learner-facing announcements cannot use the JSON:API `messages` resource:
 * `-my` scopes restrict that collection to rows the caller authored, so
 * admin-written announcements would never appear. The REST endpoint is the
 * published learner path.
 */
export type CohortAnnouncement = {
  id: string;
  title: string;
  markdown: string;
  startDate?: string;
};

type CohortAnnouncementPayload = {
  id?: unknown;
  title?: unknown;
  markdown?: unknown;
  startDate?: unknown;
};

export function cohortAnnouncementsUrl(
  host: string,
  programId: string,
  cohortId: string
) {
  return `${host}/end-user/v2/messages/announcement/program/${encodeURIComponent(programId)}/${encodeURIComponent(cohortId)}`;
}

export async function buildCohortAnnouncementsRequest(
  apis: Apis,
  session: SessionService,
  programId: string,
  cohortId: string
) {
  const init = await addCurrentAuthHeader(
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
    { session }
  );

  return new Request(
    cohortAnnouncementsUrl(apis.monolithApi.host, programId, cohortId),
    init
  );
}

function asAnnouncement(
  value: CohortAnnouncementPayload
): CohortAnnouncement | null {
  if (typeof value.id !== 'string' || typeof value.title !== 'string') {
    return null;
  }

  return {
    id: value.id,
    title: value.title,
    markdown: typeof value.markdown === 'string' ? value.markdown : '',
    startDate:
      typeof value.startDate === 'string' ? value.startDate : undefined,
  };
}

export async function parseCohortAnnouncementsResponse(
  response: Response
): Promise<CohortAnnouncement[]> {
  if (!response.ok) {
    const body = await response.text();
    throw new Error(
      `Failed to fetch cohort announcements: ${response.status} ${body}`
    );
  }

  const data: unknown = await response.json();
  if (!Array.isArray(data)) {
    return [];
  }

  return data.flatMap((item) => {
    const announcement = asAnnouncement(item as CohortAnnouncementPayload);
    return announcement ? [announcement] : [];
  });
}
